import React, { useState } from 'react';
import { Delete, Edit } from '@mui/icons-material';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { EventType } from '../../../../types';
import { eventsService } from '../../../../core/services/eventsService';
import styles from '../SchedulerSetting.module.scss';

interface EventTypesListProps {
	eventTypes: EventType[];
	onEventTypeCreated: () => void;
	onEditClick: (type: EventType) => void;
	canEdit?: boolean;
	canDelete?: boolean;
}

const EventTypesList: React.FC<EventTypesListProps> = ({
	eventTypes,
	onEventTypeCreated,
	onEditClick,
	canEdit = false,
	canDelete = false,
}) => {
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [typeToDelete, setTypeToDelete] = useState<EventType | null>(null);

	const handleDeleteClick = (type: EventType) => {
		if (!canDelete || eventTypes.length <= 1) return;
		setTypeToDelete(type);
		setDeleteDialogOpen(true);
	};

	const handleConfirmDelete = async () => {
		if (!typeToDelete) return;
		
		try {
			await eventsService.deleteEventType(typeToDelete.id);
			setDeleteDialogOpen(false);
			setTypeToDelete(null);
			onEventTypeCreated();
		} catch (e) {
			console.error('Failed to delete event type:', e);
			setDeleteDialogOpen(false);
			setTypeToDelete(null);
		}
	};

	const handleCancelDelete = () => {
		setDeleteDialogOpen(false);
		setTypeToDelete(null);
	};

	return (
		<>
			<div className={styles.tableContainer}>
				<table className={styles.workplaceTable}>
					<thead>
						<tr>
							<th>Type</th>
							<th style={{ textAlign: 'center' }}>Reservations</th>
							<th style={{ width: 48, textAlign: 'center' }}>Edit</th>
							<th style={{ width: 48, textAlign: 'center' }}>Delete</th>
						</tr>
					</thead>
					<tbody>
						{eventTypes.length === 0 ? (
							<tr>
								<td colSpan={4} style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
									No records found
								</td>
							</tr>
						) : (
							eventTypes.map((type) => (
								<tr key={type.id}>
									<td>
										<span>{type.label || type.name}</span>
									</td>
									<td style={{ textAlign: 'center' }}>
										<span>{type.isReservation ? 'Yes' : 'No'}</span>
									</td>
									<td style={{ textAlign: 'center', padding: '8px', width: 48 }}>
										{(() => {
											const editDisabled = !canEdit;
											return (
										<button
											className={styles.iconButton}
											title={editDisabled ? "You do not have permission to edit" : "Edit"}
											onClick={() => !editDisabled && onEditClick(type)}
											disabled={editDisabled}
											style={{ opacity: editDisabled ? 0.5 : 1, cursor: editDisabled ? 'not-allowed' : 'pointer' }}
										>
											<Edit className={styles.editIcon} />
										</button>
											);
										})()}
									</td>
									<td style={{ textAlign: 'center', padding: '8px', width: 48 }}>
										{(() => {
											const deleteDisabled = !canDelete || eventTypes.length <= 1;
											return (
										<button
											onClick={() => !deleteDisabled && handleDeleteClick(type)}
											title={
												deleteDisabled
													? !canDelete
														? "You do not have permission to delete"
														: "Cannot delete the last type"
													: "Delete Type"
											}
											className={styles.iconButton}
											disabled={deleteDisabled}
											style={{
												opacity: deleteDisabled ? 0.5 : 1,
												cursor: deleteDisabled ? 'not-allowed' : 'pointer',
											}}
										>
											<Delete className={styles.deleteIcon} />
										</button>
											);
										})()}
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>

			{/* Delete Confirmation Dialog */}
			<Dialog
				open={deleteDialogOpen}
				onClose={handleCancelDelete}
				aria-labelledby="delete-dialog-title"
				aria-describedby="delete-dialog-description"
				maxWidth="sm"
				fullWidth
				style={{ zIndex: 9999 }}
				PaperProps={{
					style: {
						backgroundColor: 'white',
						boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
						borderRadius: '8px',
						minWidth: '400px'
					}
				}}
			>
				<DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
				<DialogContent>
					<DialogContentText id="delete-dialog-description">
						Are you sure you want to delete the event type{' '}
						{typeToDelete && <strong>&ldquo;{typeToDelete.label || typeToDelete.name}&rdquo;</strong>}? This action cannot be undone.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleCancelDelete}
						style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white', textTransform: 'none', borderRadius: '4px', padding: '5px 15px' }}
					>
						Cancel
					</Button>
					<Button
						style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white', textTransform: 'none', borderRadius: '4px', padding: '5px 15px' }}
						onClick={handleConfirmDelete}
					>
						Delete
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default EventTypesList;

